import React, {
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import FolderIcon from '@mui/icons-material/Folder';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import {SimpleTreeView, TreeItem, TreeViewBaseItem} from '@mui/x-tree-view';
import {Checkbox, Typography} from '@mui/material';
import {t} from 'i18next';
import './ArrTreeView.scss';

import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {ReactComponent as ScopeIcon} from '../../../assets/images/scope_icon.svg';
import {ReactComponent as TreeIcon} from '../../../assets/images/loads-not-indexed.svg';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import {ReactComponent as MoreIcon} from '../../../assets/images/icon-more.svg';
import ArrMenu, {ArrMenuHandle, CustomMenuItem} from '../arr-menu/ArrMenu';
import ArrButton from '../arr-button/ArrButton';
import ArrTooltip from '../arr-tooltip/ArrTooltip';

const getIconFromFileType = (fileType: string) => {
  switch (fileType) {
    case 'folder':
      return <FolderIcon className="location-folder-icon" />;
    case 'search':
      return <SearchOutlinedIcon className="location-folder-icon" />;
    case 'search_private':
      return <PersonSearchOutlinedIcon className="location-folder-icon" />;
    case 'loads_not_indexed':
      return <TreeIcon className="location-tree-icon" />;
  }
};

export type TrackItemsType = {
  label: string;
  id: string;
};

interface IArrLazyTreeViewProps {
  itemsList: TreeViewBaseItem<ExtendedTreeItemProps>[];
  setSelectedItem?: (
    value: string[] | null,
    isCtrlPressed?: boolean,
    node?: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => void;
  selectedItem?: string;
  treeSelectedItems?: string[] | null;
  multipleSelection?: boolean;
  isShowCountLabel?: boolean;
  handleNoResultMessage?: string;
  allowGlobalSelect?: boolean;
  expandedNodes?: string[];
  collapsedNodes?: string[];
  onExpandedNodesChange?: (expandedNodes: string[]) => void;
  onCollapsedNodesChange?: (collapsedNodes: string[]) => void;
  setExpandedFolders?: (data: string[]) => void;
  showActions?: boolean;
  showCheckmark?: boolean;
  selectedNodeToMove?: string;
  updateTreeData?: React.Dispatch<
    React.SetStateAction<TreeViewBaseItem<ExtendedTreeItemProps>[] | undefined>
  >;
  isShowCheckbox?: boolean;
  MoreMenuOptions?: CustomMenuItem[];
  onMenuItemClick?: (item: CustomMenuItem) => void;
  customContentClass?: string;
  customContentHeight?: number;
  onNodeExpand?: (
    nodeId: string
  ) => Promise<TreeViewBaseItem<ExtendedTreeItemProps>[]>;
  onNodeCollapse?: (nodeId: string) => void;
  loadingNodes?: Set<string>;
  indeterminateNodeIds?: string[]; // Node IDs that should show as indeterminate state
  checkboxDataTestId?: string;
}

interface TreeItem {
  id: string;
  label: string;
  fileType: string;
  children?: TreeItem[];
}

const ArrLazyTreeView = (props: IArrLazyTreeViewProps) => {
  const {
    itemsList,
    setSelectedItem,
    selectedItem,
    treeSelectedItems,
    multipleSelection,
    isShowCountLabel = true,
    handleNoResultMessage,
    expandedNodes = [],
    onExpandedNodesChange,
    onCollapsedNodesChange,
    showActions = false,
    showCheckmark = false,
    selectedNodeToMove,
    updateTreeData,
    isShowCheckbox = false,
    MoreMenuOptions,
    onMenuItemClick,
    customContentHeight,
    customContentClass = '',
    onNodeExpand,
    indeterminateNodeIds = [],
    checkboxDataTestId = 'arr-lazy-tree-view',
  } = props;

  const [selectedItems, setSelectedItems] = useState<
    string | string[] | null | undefined
  >(selectedItem);
  const [treeData, setTreeData] = useState(itemsList);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<string[]>(expandedNodes);

  const menuRef1 = useRef<ArrMenuHandle | null>(null);
  // Skip notifying parent only when treeData changed because we synced from itemsList.
  // This prevents an infinite loop when parent passes itemsList that gets a new reference every
  // render. updateTreeData is still called on initial mount and when treeData changes from
  // user actions (e.g. onNodeExpand).
  const skipNextUpdateTreeDataRef = useRef(false);
  // Keep a ref to the last tree that had loaded children so we never replace it with placeholder
  // when parent re-renders and passes a new reference a few ms later (arrows vanishing fix).
  const richTreeRef = useRef<TreeViewBaseItem<ExtendedTreeItemProps>[] | null>(
    null
  );

  // Helpers to avoid replacing treeData with initial placeholder tree when we already have loaded children
  const hasLoadedChildren = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): boolean => {
    for (const node of nodes) {
      if (node.isChildrenLoaded) return true;
      if (node.children?.length) {
        const realChildren = node.children.filter(
          (c) => !c.id?.includes('-loading')
        );
        if (realChildren.length > 0 && hasLoadedChildren(realChildren))
          return true;
      }
    }
    return false;
  };
  // True if any node has expand icon (hasChildren or children) so we preserve when all are collapsed
  const hasExpandableNodes = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): boolean => {
    for (const node of nodes) {
      if (node.hasChildren || (node.children && node.children.length > 0))
        return true;
    }
    return false;
  };
  const getRootIds = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) =>
    nodes.map((n) => n.id).filter((id) => !id?.includes('-loading'));
  const sameRootIds = (
    a: TreeViewBaseItem<ExtendedTreeItemProps>[],
    b: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ) => {
    const idA = getRootIds(a);
    const idB = getRootIds(b);
    return idA.length === idB.length && idA.every((id, i) => id === idB[i]);
  };

  useEffect(() => {
    if (skipNextUpdateTreeDataRef.current) {
      skipNextUpdateTreeDataRef.current = false;
      return;
    }
    if (updateTreeData) {
      updateTreeData(treeData);
    }
  }, [treeData, updateTreeData]);

  useEffect(() => {
    setTreeData((prev) => {
      const sameRoots =
        itemsList.length > 0 && prev.length > 0 && sameRootIds(prev, itemsList);
      const incomingIsPlaceholder = sameRoots && !hasLoadedChildren(itemsList);

      // Never replace our tree when parent sends placeholder (same roots, no loaded children).
      // This keeps expand icons on children (e.g. after uncheck when no child is expanded).
      if (incomingIsPlaceholder) {
        richTreeRef.current = prev;
        return prev;
      }

      // Persist tree in ref when it has loaded or expandable structure
      if (
        prev.length > 0 &&
        (hasLoadedChildren(prev) || hasExpandableNodes(prev))
      )
        richTreeRef.current = prev;
      if (
        itemsList.length > 0 &&
        (hasLoadedChildren(itemsList) || hasExpandableNodes(itemsList))
      )
        richTreeRef.current = itemsList;

      return itemsList;
    });
    skipNextUpdateTreeDataRef.current = true;
    if (itemsList.length == 1 && !expandedNodes.length) {
      const id = itemsList.map((item) => item.id);
      setExpandedIds(id);
    }
  }, [itemsList]);

  useEffect(() => {
    if (multipleSelection) {
      setSelectedItems(treeSelectedItems);
    } else {
      setSelectedItems(selectedItem);
    }
  }, [multipleSelection, selectedItem, treeSelectedItems]);

  const handleSelectedItemsChange = (
    event: SyntheticEvent<Element, Event> | null,
    ids: string[] | string | null
  ) => {
    const isCtrlPressed = Boolean(
      !!event &&
      (('ctrlKey' in event && event.ctrlKey) ||
        ('nativeEvent' in event &&
          'ctrlKey' in event.nativeEvent &&
          (event.nativeEvent as MouseEvent | KeyboardEvent).ctrlKey))
    );

    const clickedIds = Array.isArray(ids) ? ids : ids ? [ids] : [];
    const clickedId = clickedIds[0];

    if (!clickedId) return;

    const selectedNode = findNodeById(treeData, clickedId);
    if (!selectedNode || selectedNode.disabled) return;

    if (showCheckmark) {
      const parentFolderId = findParentById(treeData, selectedNodeToMove ?? '');

      if (
        clickedId === selectedNodeToMove ||
        clickedId === parentFolderId ||
        selectedNode.fileType !== 'folder'
      ) {
        return;
      }
    }

    const isValidId = (id: string) =>
      id && !id.includes('loading') && id !== selectedNodeToMove;

    if (!isValidId(clickedId)) return;

    if (isCtrlPressed) {
      let newSelection = Array.isArray(selectedItems)
        ? [...selectedItems]
        : selectedItems
          ? [selectedItems?.toString()]
          : [];

      if (newSelection.includes(clickedId)) {
        newSelection = newSelection.filter((id) => id !== clickedId);
      } else {
        newSelection.push(clickedId);
      }

      setSelectedItems(newSelection);
      if (setSelectedItem)
        setSelectedItem(newSelection, isCtrlPressed, selectedNode);
    } else if (multipleSelection) {
      let newSelection = Array.isArray(selectedItems) ? [...selectedItems] : [];

      if (newSelection.includes(clickedId)) {
        newSelection = newSelection.filter((id) => id !== clickedId);
      } else {
        newSelection.push(clickedId);
      }

      setSelectedItems(newSelection);
      if (setSelectedItem)
        setSelectedItem(newSelection, isCtrlPressed, selectedNode);
    } else {
      setSelectedItems(clickedId);
      if (setSelectedItem)
        setSelectedItem([clickedId], isCtrlPressed, selectedNode);
    }
  };

  const isNodeSelected = (nodeId: string) => {
    let result = false;
    if (Array.isArray(selectedItems)) {
      result = selectedItems.includes(nodeId);
      return result;
    }
    result = selectedItems === nodeId;
    return result;
  };

  // Helper function to get all descendant leaf nodes recursively from a specific node
  const getAllDescendantLeaves = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ): string[] => {
    const result: string[] = [];
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        // Skip loading placeholders
        if (child.id && child.id.includes('-loading')) continue;
        // If child has no children, it's a leaf
        if (!child.children || child.children.length === 0) {
          result.push(child.id);
        } else {
          // Recursively get leaves from this child
          const childLeaves = getAllDescendantLeaves(child);
          result.push(...childLeaves);
        }
      }
    }
    return result;
  };

  // Helper function to find all direct children of a parent node by searching entire tree
  // This is needed when children aren't loaded in the parent's children array
  const findDirectChildrenInTree = (
    parentId: string,
    searchNodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    const result: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

    const searchRecursive = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ) => {
      for (const node of nodes) {
        // Check if this node is the parent we're looking for
        if (node.id === parentId) {
          // Found the parent, return its direct children (if any)
          if (node.children && node.children.length > 0) {
            const realChildren = node.children.filter(
              (c) => c && !c.id?.includes('-loading')
            );
            result.push(...realChildren);
          }
          return;
        }
        // Recursively search children
        if (node.children && node.children.length > 0) {
          searchRecursive(node.children);
        }
      }
    };

    searchRecursive(searchNodes);
    return result;
  };

  const getSelectionState = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ): {
    checked: boolean;
    indeterminate: boolean;
  } => {
    const nodeIsSelected = isNodeSelected(node.id);
    const selectedItemsArray = Array.isArray(selectedItems)
      ? selectedItems
      : selectedItems
        ? [selectedItems]
        : [];

    // If children are not loaded yet, check if the node itself is selected
    // This handles the case where parent is selected but children haven't been loaded
    // Check: isChildrenLoaded is explicitly false/undefined, OR hasChildren is true but no real children exist
    const hasRealChildren =
      node.children &&
      node.children.length > 0 &&
      node.children.some((c) => c && !c.id?.includes('-loading'));

    if (
      node.isChildrenLoaded === false ||
      node.isChildrenLoaded === undefined ||
      (node.hasChildren && !hasRealChildren)
    ) {
      const isInSelectedItems = selectedItemsArray.includes(node.id);

      // If this is a parent node (hasChildren), we need to check children state
      if (node.hasChildren) {
        // Check if this is a nested parent scenario (root parent with child parents)
        // First try to get direct children from node.children
        let directChildren =
          node.children?.filter((c) => c && !c.id?.includes('-loading')) || [];

        // If no children found in node.children, search entire tree structure
        if (directChildren.length === 0) {
          directChildren = findDirectChildrenInTree(node.id, treeData);
        }

        const hasChildParents = directChildren.some(
          (child) => child.hasChildren
        );

        if (hasChildParents && directChildren.length > 0) {
          // This is a root parent with child parents (children are loaded)
          // Calculate state based on whether child parents have selected descendant leaves
          let total = 0;
          let selected = 0;

          for (const childParent of directChildren) {
            total += 1;

            // Get all descendant leaves from this child parent
            const descendantLeaves = getAllDescendantLeaves(childParent);

            // If no leaves found from childParent.children, search entire tree
            if (descendantLeaves.length === 0) {
              const foundChildren = findDirectChildrenInTree(
                childParent.id,
                treeData
              );
              for (const child of foundChildren) {
                const childLeaves = getAllDescendantLeaves(child);
                descendantLeaves.push(...childLeaves);
              }
            }

            // Check if any descendant leaf is in selectedItems
            const hasSelectedDescendant =
              descendantLeaves.length > 0 &&
              descendantLeaves.some((leafId) =>
                selectedItemsArray.includes(leafId)
              );

            if (hasSelectedDescendant) {
              selected += 1;
            }
          }

          const allSelected = selected === total && total > 0;
          const noneSelected = selected === 0;

          return {
            checked: allSelected,
            indeterminate: !allSelected && !noneSelected,
          };
        }

        // For root parent with unloaded children: check if state was calculated in DocumentSelection.tsx
        if (directChildren.length === 0 && node.hasChildren) {
          // Check if root parent ID is in indeterminateNodeIds (passed from DocumentSelection.tsx)
          const isIndeterminate =
            Array.isArray(indeterminateNodeIds) &&
            indeterminateNodeIds.includes(node.id);

          // If root parent is explicitly in selectedItems, show as checked
          // This happens when DocumentSelection.tsx calculates and adds root parent IDs
          if (isInSelectedItems) {
            return {
              checked: true,
              indeterminate: false,
            };
          }

          // If root parent is in indeterminateNodeIds, show as indeterminate
          if (isIndeterminate) {
            return {
              checked: false,
              indeterminate: true,
            };
          }

          // Otherwise, can't determine state without children being loaded
          // Return unchecked - state will be calculated correctly once children load
          return {
            checked: false,
            indeterminate: false,
          };
        }

        // For regular parents (not nested), check descendant leaves
        // First try to get descendant leaves from the node's direct children
        const descendantLeaves = getAllDescendantLeaves(node);

        // If no children found in node.children, search entire tree structure
        if (descendantLeaves.length === 0) {
          const foundDirectChildren = findDirectChildrenInTree(
            node.id,
            treeData
          );
          // Get all descendant leaves from found children
          for (const child of foundDirectChildren) {
            const childLeaves = getAllDescendantLeaves(child);
            descendantLeaves.push(...childLeaves);
          }
        }

        if (descendantLeaves.length > 0) {
          // We found some children in the tree structure
          const selectedLeaves = descendantLeaves.filter((leafId) =>
            selectedItemsArray.includes(leafId)
          );

          const allLeavesSelected =
            selectedLeaves.length === descendantLeaves.length &&
            selectedLeaves.length > 0;
          const someLeavesSelected =
            selectedLeaves.length > 0 &&
            selectedLeaves.length < descendantLeaves.length;

          return {
            checked: allLeavesSelected,
            indeterminate:
              someLeavesSelected || (isInSelectedItems && !allLeavesSelected),
          };
        } else {
          // Can't find children in tree structure (they're truly not loaded)
          // For root parent with unloaded children: return unchecked (not indeterminate)
          // The state will be calculated correctly once children are loaded
          return {
            checked: false,
            indeterminate: false,
          };
        }
      }

      // Not a parent node, just check if selected
      return {
        checked: nodeIsSelected,
        indeterminate: false,
      };
    }

    // If no children or empty children array, check if node itself is selected
    if (!node.children || node.children.length === 0) {
      return {
        checked: nodeIsSelected,
        indeterminate: false,
      };
    }

    // Filter out placeholder loading children
    const realChildren = node.children.filter(
      (c) => c && !c.id?.includes('-loading')
    );

    // If only placeholder children exist, check if node itself is selected
    if (realChildren.length === 0) {
      // If this is a parent node, check children state similar to unloaded case
      if (node.hasChildren) {
        // Check if this is a nested parent scenario (root parent with child parents)
        const directChildren = findDirectChildrenInTree(node.id, treeData);
        const hasChildParents = directChildren.some(
          (child) => child.hasChildren
        );

        if (hasChildParents && directChildren.length > 0) {
          // This is a root parent with child parents (only placeholders in node.children)
          // Calculate state based on whether child parents have selected descendant leaves
          let total = 0;
          let selected = 0;

          for (const childParent of directChildren) {
            total += 1;

            // Get all descendant leaves from this child parent
            const descendantLeaves = getAllDescendantLeaves(childParent);

            // If no leaves found, search entire tree for this child parent's children
            if (descendantLeaves.length === 0) {
              const foundChildren = findDirectChildrenInTree(
                childParent.id,
                treeData
              );
              for (const child of foundChildren) {
                const childLeaves = getAllDescendantLeaves(child);
                descendantLeaves.push(...childLeaves);
              }
            }

            // Check if any descendant leaf is in selectedItems
            const hasSelectedDescendant =
              descendantLeaves.length > 0 &&
              descendantLeaves.some((leafId) =>
                selectedItemsArray.includes(leafId)
              );

            if (hasSelectedDescendant) {
              selected += 1;
            }
          }

          const allSelected = selected === total && total > 0;
          const noneSelected = selected === 0;

          return {
            checked: allSelected,
            indeterminate: !allSelected && !noneSelected,
          };
        }

        // For regular parents (not nested), check descendant leaves
        // First try to get descendant leaves from the node's direct children
        const descendantLeaves = getAllDescendantLeaves(node);

        // If no children found in node.children, search entire tree structure
        if (descendantLeaves.length === 0) {
          const foundDirectChildren = findDirectChildrenInTree(
            node.id,
            treeData
          );
          // Get all descendant leaves from found children
          for (const child of foundDirectChildren) {
            const childLeaves = getAllDescendantLeaves(child);
            descendantLeaves.push(...childLeaves);
          }
        }

        if (descendantLeaves.length > 0) {
          const selectedLeaves = descendantLeaves.filter((leafId) =>
            selectedItemsArray.includes(leafId)
          );
          const allLeavesSelected =
            selectedLeaves.length === descendantLeaves.length &&
            selectedLeaves.length > 0;
          const someLeavesSelected =
            selectedLeaves.length > 0 &&
            selectedLeaves.length < descendantLeaves.length;

          return {
            checked: allLeavesSelected,
            indeterminate:
              someLeavesSelected || (nodeIsSelected && !allLeavesSelected),
          };
        } else {
          // Can't find children, parent selected means some children selected
          // For nested parents: return indeterminate so parent can count it correctly
          return {
            checked: false,
            indeterminate: nodeIsSelected,
          };
        }
      }

      return {
        checked: nodeIsSelected,
        indeterminate: false,
      };
    }

    // Calculate selection state based on real children
    // This is the main logic for parents with loaded children
    let total = 0;
    let selected = 0;
    const childStates: Array<{
      id: string;
      label: string;
      checked: boolean;
      indeterminate: boolean;
      isSelected: boolean;
    }> = [];

    for (const child of realChildren) {
      const childState = getSelectionState(child);
      const childIsSelected = isNodeSelected(child.id);
      total += 1;
      childStates.push({
        id: child.id,
        label: child.label,
        checked: childState.checked,
        indeterminate: childState.indeterminate,
        isSelected: childIsSelected,
      });
      // Count both checked and indeterminate as "selected" for parent calculation
      // This ensures that if all child parents are selected (checked OR indeterminate),
      // the root parent shows as checked
      if (childState.checked || childState.indeterminate) {
        selected += 1;
      }
    }

    const allSelected = selected === total && total > 0;
    const noneSelected = selected === 0;

    return {
      checked: allSelected,
      indeterminate: !allSelected && !noneSelected,
    };
  };

  const getAllChildNodeIds = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ): string[] => {
    if (!node.children) return [];
    return node.children.reduce<string[]>(
      (acc, child) => [...acc, child.id, ...getAllChildNodeIds(child)],
      []
    );
  };

  const handleCheckboxSelection = (checked: boolean, nodeId: string) => {
    const node = findNodeById(treeData, nodeId);
    if (!node) return;

    const childIds = getAllChildNodeIds(node);
    const nodeIdsToChange = [nodeId, ...childIds];

    setSelectedItems((prev) => {
      const current = Array.isArray(prev) ? [...prev] : prev ? [prev] : [];

      if (checked) {
        const newSelection = Array.from(
          new Set([...current, ...nodeIdsToChange])
        );
        if (setSelectedItem) setSelectedItem(newSelection);
        return newSelection;
      } else {
        const newSelection = current.filter(
          (id) => !nodeIdsToChange.includes(id)
        );
        if (setSelectedItem) setSelectedItem(newSelection);
        return newSelection;
      }
    });
  };

  const handleMoreIconClick = (e: React.MouseEvent<HTMLElement>) => {
    menuRef1.current?.open(e.currentTarget as HTMLElement);
  };

  const handleNodeExpand = useCallback(
    async (nodeId: string) => {
      if (onNodeExpand) {
        try {
          const children = await onNodeExpand(nodeId);
          setTreeData((prevData) => {
            return updateNodeChildren(prevData, nodeId, children);
          });
        } catch (error) {
          console.error('Error loading children for node:', nodeId, error);
        }
      }
    },
    [onNodeExpand]
  );

  const updateNodeChildren = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    nodeId: string,
    children: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
    return nodes.map((node) => {
      if (node.id === nodeId) {
        return {...node, isChildrenLoaded: true, children};
      }
      if (node.children) {
        return {
          ...node,
          children: updateNodeChildren(node.children, nodeId, children),
        };
      }
      return node;
    });
  };

  const renderTree = (nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]) => {
    const newFolders = nodes.filter(
      (node) => node.fileType === 'folder' && node.newFolder
    );
    const folders = nodes.filter(
      (node) => node.fileType === 'folder' && !node.newFolder
    );
    const files = nodes.filter((node) => node.fileType !== 'folder');
    const newArray = [...newFolders, ...folders, ...files];

    return newArray.map((node, index) => {
      const isHovered = hoveredNode == node.id;
      return (
        <span
          className="tooltip-padding"
          key={node.id}
          data-testid={`arr-lazy-tree-view-span-${node.id}`}
        >
          <TreeItem
            sx={{
              paddingLeft: `calc(6px+var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth)})`,
            }}
            slotProps={{
              content: {
                className: `tree-item-content ${customContentClass || ''} ${node.added && 'tree-item-added'}`,
                style: {
                  height: customContentHeight || '',
                },
              },
            }}
            key={node.id}
            itemId={node.id}
            label={
              <div
                className="location-treeitem-label"
                data-testid={`arr-lazy-tree-label-${node.id}`}
              >
                <div
                  className={`location-treeitem ${node.newFolder ? 'new-input' : ''} `}
                  data-testid={`arr-lazy-tree-view-cb-wrapper-${checkboxDataTestId}-${node.id}`}
                >
                  {isShowCheckbox
                    ? (() => {
                        const {checked, indeterminate} =
                          getSelectionState(node);
                        return (
                          <Checkbox
                            data-testid={`${checkboxDataTestId}-node-checkbox-${String(node.id).replace(/[^a-zA-Z0-9_-]/g, '-')}`}
                            className="tree-checkbox"
                            size="small"
                            checked={checked}
                            indeterminate={indeterminate}
                            onChange={(e) => {
                              handleCheckboxSelection(
                                e.target.checked,
                                node.id
                              );
                            }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!!node?.disabled}
                          />
                        );
                      })()
                    : null}

                  {getIconFromFileType(node.fileType || '')}
                  {node.isScopeSelected && (
                    <ScopeIcon className={'location-folder-icon scope-icon'} />
                  )}

                  <Typography
                    className="typo-text body3_size custom-text-style full-width-text"
                    sx={{flex: 1}}
                    component="span"
                    data-testid={`arr-lazy-tree-view-typography-text-${node.id}`}
                  >
                    <div
                      className={`${node?.disabled ? 'label-disable' : ''} `}
                      data-testid={`arr-lazy-tree-view-div-button`}
                    >
                      <div
                        className="label-container"
                        data-testid={`arr-lazy-tree-view-div-icon`}
                      >
                        <ArrTooltip content={node.label}>
                          <Typography
                            className={`typo-text body3_size ${node?.disabled && 'label-disable'} ${node.isScopeSelected && 'scope-label'}`}
                            data-testid={`arr-lazy-tree-view-node-label-${node.id}`}
                          >
                            {node.label}
                          </Typography>
                        </ArrTooltip>
                        {node.count &&
                          node.fileType !== 'folder' &&
                          isShowCountLabel && (
                            <span
                              className={
                                node?.disabled
                                  ? 'label-disable'
                                  : 'count-active'
                              }
                              data-testid={`arr-lazy-tree-view-span-text`}
                            >{`  (${node.count})`}</span>
                          )}
                      </div>
                      {node?.notYetIndexed && (
                        <div data-testid={`arr-lazy-tree-view-not-yet-indexed`}>
                          <Typography
                            className="typo-text body3_size count-active"
                            data-testid={`arr-lazy-tree-view-not-yet-indexed--${node.id}`}
                          >
                            {node?.notYetIndexed}{' '}
                            {t('common.treeView.notYetIndexed')}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Typography>
                </div>
                <div
                  className="tree-action-container"
                  data-testid={`arr-lazy-tree-view-div-link`}
                >
                  {showActions && node?.showActionButtonWithLabel ? (
                    <div
                      className="tree-button-container"
                      data-testid={`arr-lazy-tree-view-div-input`}
                    >
                      <Typography
                        className="typo-text body3_size"
                        data-testid={`arr-lazy-tree-view-action-label-${node.id}`}
                      >
                        {node?.actionButtonLabel}
                      </Typography>
                      <ArrButton
                        size="small"
                        variant="primary"
                        dataTestId={`arr-lazy-tree-view-button-${node?.id}`}
                      >
                        {node?.actionButtonName}
                      </ArrButton>
                    </div>
                  ) : null}
                  {showActions && node?.showProgress ? (
                    <div
                      className="project-bar-percentage"
                      data-testid={`arr-lazy-tree-view-`}
                    >
                      <div
                        className="progress-bar-container"
                        data-testid={`arr-lazy-tree-view-div-text`}
                      >
                        <div
                          className="progress-bar-fill"
                          style={{width: `${node.progressPercentage}%`}}
                          data-testid={`arr-lazy-tree-view-progress-${node.id}`}
                        ></div>
                      </div>
                      <Typography
                        className="typo-text body3_size"
                        data-testid={`arr-lazy-tree-view--text`}
                      >
                        {node.progressPercentage}%
                      </Typography>
                    </div>
                  ) : null}

                  <div
                    className="more-action-container"
                    data-testid={`arr-lazy-tree-view-div-2`}
                  >
                    {showActions && isHovered && node?.showMoreOptions ? (
                      <div
                        data-testid={`arr-lazy-tree-view-more-actions-${node.id}`}
                      >
                        {!node?.isDefault && (
                          <>
                            <ArrIconButton
                              onClick={(e) => handleMoreIconClick(e)}
                              icon={<MoreIcon className="more-icon" />}
                              ariaLabel=""
                              shape="none"
                              className="more-icon-button"
                              testId={`$arr-lazy-tree-view-icon-button-more-${node.id}`}
                            />
                            <ArrMenu
                              id="more-menu"
                              ref={menuRef1}
                              items={MoreMenuOptions || []}
                              onItemClick={onMenuItemClick}
                              direction="left"
                            />
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            }
          >
            {((node.isChildrenLoaded && node.hasChildren) ||
              (node.hasChildren &&
                node.children &&
                node.children?.length > 0)) &&
              node.children &&
              renderTree(node.children)}
          </TreeItem>
        </span>
      );
    });
  };

  const findNodeById = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    id: string
  ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
    for (const node of nodes) {
      if (node.id === id) return node;
      if (node.children) {
        const found = findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const findParentById = (
    nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
    nodeId: string
  ): string | null => {
    // Recursive search through children
    for (const node of nodes) {
      if (node.children?.some((child) => child.id === nodeId)) {
        return node.id;
      }
      if (node.children) {
        const parent = findParentById(node.children, nodeId);
        if (parent) return parent;
      }
    }
    return null;
  };

  const handleExpandedItemsChange = async (
    event: React.SyntheticEvent | null,
    ids: string[]
  ) => {
    const isCtrlPressed =
      !!event &&
      ((event as React.SyntheticEvent & {ctrlKey?: boolean}).ctrlKey ||
        (
          event as React.SyntheticEvent & {
            nativeEvent?: MouseEvent | KeyboardEvent;
          }
        ).nativeEvent?.ctrlKey === true);

    if (event) {
      event.stopPropagation();
    }

    const newlyCollapsed = expandedIds.filter((id) => !ids.includes(id));
    const newlyExpanded = ids.filter((id) => !expandedIds.includes(id));

    if (isCtrlPressed) {
      let newSelection = Array.isArray(selectedItems)
        ? [...selectedItems]
        : selectedItems
          ? [selectedItems?.toString()]
          : [];

      if (newSelection.includes(newlyExpanded[0])) {
        newSelection = newSelection.filter((id) => id !== newlyExpanded[0]);
      } else {
        newSelection.push(newlyExpanded[0]);
      }
      setSelectedItems(newSelection);
    } else {
      setExpandedIds(ids);
      const currentNode = findNodeById(treeData, newlyExpanded[0]);
      if (currentNode && !currentNode?.isChildrenLoaded)
        await handleNodeExpand(currentNode?.id);

      if (onExpandedNodesChange) {
        onExpandedNodesChange(ids);
      }
      if (onCollapsedNodesChange) {
        onCollapsedNodesChange(newlyCollapsed);
      }
    }
  };

  return (
    <>
      {treeData && treeData.length > 0 ? (
        <SimpleTreeView
          className="trr-item-check"
          sx={{
            '& .MuiTreeItem-content[data-focused="true"]': {
              backgroundColor: 'transparent !important',
            },
          }}
          selectedItems={selectedItems}
          multiSelect={multipleSelection}
          onSelectedItemsChange={
            !isShowCheckbox ? handleSelectedItemsChange : undefined
          }
          itemChildrenIndentation={24}
          expansionTrigger="iconContainer"
          onExpandedItemsChange={handleExpandedItemsChange}
          expandedItems={expandedIds}
          onItemFocus={(event: React.SyntheticEvent | null, itemId: string) => {
            setHoveredNode(itemId);
          }}
        >
          {renderTree(treeData)}
        </SimpleTreeView>
      ) : (
        <Typography
          className="typo-text body3_size custom-text"
          data-testid={`arr-lazy-tree-view-no-result-button`}
        >
          {handleNoResultMessage}
        </Typography>
      )}
    </>
  );
};

export default ArrLazyTreeView;
